import { css } from '@compiled/react';

import { wacToken } from '@/libraries/design/foundations';

export interface UniformMigrationPlaceholderProps {
	suggestedComponent?: string;
	reviewerTag?: string;
	confidence?: number;
	confidenceRationale?: string;
	sectionName?: string;
	sectionId?: string;
	sourcePageUrl?: string;
	sourceText?: string;
	sourceImages?: string;
	sourceStyleJson?: string;
	sourceComponentRaw?: string;
	alternativesConsidered?: string;
}

/**
 * Migration Placeholder component.
 *
 * Rendered by the AI migration agent when a Magnolia section cannot be mapped
 * confidently to a Uniform component. In production (NODE_ENV === "production")
 * this renders nothing so that placeholder sections are invisible to end-users.
 * In all other environments (dev, staging, preview) it renders a clearly-visible
 * red banner showing the suggested component name and a link to the source page,
 * so that reviewers can quickly identify and replace it.
 */
const UniformMigrationPlaceholder = ({
	suggestedComponent,
	reviewerTag,
	confidence,
	sectionName,
	sectionId,
	sourcePageUrl,
}: UniformMigrationPlaceholderProps) => {
	// Render nothing in production — placeholders must not be visible to end-users.
	if (process.env['NODE_ENV'] === 'production') {
		return null;
	}

	const confidenceLabel =
		confidence !== undefined && confidence !== null
			? `${Math.round(confidence * 100)}%`
			: 'unknown';

	return (
		<div
			css={css({
				backgroundColor: wacToken('color.secondary.red'),
				color: wacToken('color.neutral.light0'),
				fontFamily: 'monospace',
				fontSize: '13px',
				padding: '12px 16px',
				border: `2px dashed ${wacToken('color.neutral.light0')}`,
				margin: '8px 0',
				borderRadius: '4px',
			})}
			data-testid="migration-placeholder"
			data-section-id={sectionId}
		>
			<div
				css={css({
					fontWeight: 700,
					fontSize: '15px',
					marginBlockEnd: '6px',
				})}
			>
				⚠️ Migration Placeholder
			</div>
			<div>
				<strong>Suggested component:</strong> {suggestedComponent ?? '(none)'}
			</div>
			{sectionName && (
				<div>
					<strong>Section:</strong> {sectionName}
				</div>
			)}
			{reviewerTag && (
				<div>
					<strong>Reviewer:</strong> {reviewerTag}
				</div>
			)}
			<div>
				<strong>AI confidence:</strong> {confidenceLabel}
			</div>
			{sourcePageUrl && (
				<div css={css({ marginBlockStart: '6px' })}>
					<strong>Source:</strong>{' '}
					<a
						href={sourcePageUrl}
						target="_blank"
						rel="noreferrer noopener"
						css={css({
							color: wacToken('color.extended.orange100'),
							wordBreak: 'break-all',
						})}
					>
						{sourcePageUrl}
					</a>
				</div>
			)}
		</div>
	);
};

export default UniformMigrationPlaceholder;
